<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui';
import type { AllowedResources } from '#layers/access/utils/entitlement-codec';
import type { MonospaceWorkspace, ResolvedMonospaceServiceAccount, Uuid } from '#layers/data-model/public';
import type { SystemEntityStore } from '#layers/runtime/public';
import { storeToRefs } from 'pinia';
import { useEmitAsProps } from 'reka-ui';
import { computed, defineAsyncComponent, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApiKeys } from '#layers/access/composables/use-api-keys';
import { useEntitlement } from '#layers/access/composables/use-entitlement';
import { useServiceAccountWorkspaceRoles } from '#layers/access/composables/use-service-account-workspace-roles';
import { useServiceAccountWorkspaces } from '#layers/access/composables/use-service-account-workspaces';
import { canAssignRole } from '#layers/access/constants/entitlements';
import { allResourcesAllowed } from '#layers/access/utils/entitlement-codec';
import { useWorkspaceRuntimeHandle } from '#layers/runtime/public';
import { ButtonRemove, ListCard } from '#layers/ui/public';
import { useWorkspaceDisplayNames, useWorkspaceStore } from '#layers/workspace/public';

type ServiceAccountCollection = NonNullable<SystemEntityStore['MonospaceServiceAccount']>;
type ServiceAccountCreateForm = ReturnType<ServiceAccountCollection['createForm']>;
type ServiceAccountUpdateForm = Awaited<ReturnType<ServiceAccountCollection['updateForm']>>;

const {
	serviceAccount,
	prefillWorkspaces,
	form,
	isEdit,
} = defineProps<{
	isEdit?: boolean;
	form: ServiceAccountCreateForm | ServiceAccountUpdateForm;
	serviceAccount: ResolvedMonospaceServiceAccount | null;
	prefillWorkspaces?: MonospaceWorkspace[];
}>();

const emit = defineEmits<{
	close: [result: boolean | ResolvedMonospaceServiceAccount];
}>();

/**
 * Only rendered when editing an existing account. Loading it lazily also keeps this dialog out of
 * the API key components' static import graph, so they can open the create flow through
 * `useServiceAccountDialogs` without forming a cycle.
 */
const LazyApiKeyList = defineAsyncComponent(() => import('#layers/access/components/ApiKeys/ApiKeyList.vue'));

const forwarded = useEmitAsProps(emit);
const { t } = useI18n();

const workspaces = computed<MonospaceWorkspace[]>(() => {
	if (!serviceAccount) return prefillWorkspaces ?? [];

	return serviceAccount.workspaces
		.map((member) => member.workspace)
		.filter((workspace) => workspace !== undefined);
});

const {
	availableWorkspaces,
	assignedWorkspaces,
	addWorkspace,
	removeWorkspace,
	isAddedWorkspace,
	hasWorkspaceChanges,
	saveChanges,
} = useServiceAccountWorkspaces(() => serviceAccount, workspaces);
const { getWorkspaceDisplayName } = useWorkspaceDisplayNames();

const { currentWorkspace } = storeToRefs(useWorkspaceStore());
const { allowedIds } = useEntitlement();
const allowedRoleIds = computed(() => allowedIds(canAssignRole()));

// Organization settings open this dialog with no active workspace, and a new account has no
// roles to edit yet; only an existing member of the active workspace binds a workspace handle.
// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- decided once per dialog: create vs. edit, and the form object stays the same reactive instance
const workspaceHandle = currentWorkspace.value && serviceAccount ? useWorkspaceRuntimeHandle() : null;

const { selectedRoleIds, hasRoleChanges, captureRoleChanges, saveRoleChanges } = useServiceAccountWorkspaceRoles(
	() => serviceAccount,
	workspaceHandle,
);

const newWorkspaceRoles = reactive<Record<string, string[]>>({});

function isCurrentWorkspaceRow(workspace: MonospaceWorkspace) {
	return !!currentWorkspace.value && workspace.id === currentWorkspace.value.id;
}

const isCurrentWorkspaceExistingMember = computed(() =>
	!!serviceAccount && workspaces.value.some((workspace) => isCurrentWorkspaceRow(workspace)),
);

const isCurrentWorkspaceAssigned = computed(() =>
	assignedWorkspaces.value.some((workspace) => isCurrentWorkspaceRow(workspace)),
);

// Show a role picker only where roles can be saved: a workspace being added, or the ambient workspace's
// existing membership. Existing memberships in other workspaces can't be edited from here yet.
function showRolePicker(workspace: MonospaceWorkspace) {
	return isAddedWorkspace(workspace) || (isCurrentWorkspaceExistingMember.value && isCurrentWorkspaceRow(workspace));
}

function rolesFor(workspace: MonospaceWorkspace): string[] {
	return isAddedWorkspace(workspace) ? (newWorkspaceRoles[workspace.id] ?? []) : selectedRoleIds.value;
}

function setRolesFor(workspace: MonospaceWorkspace, roleIds: string[]) {
	if (isAddedWorkspace(workspace)) {
		newWorkspaceRoles[workspace.id] = roleIds;
	}
	else {
		selectedRoleIds.value = roleIds;
	}
}

// Entitlement scoping is only accurate for the ambient workspace; other workspaces rely on the backend
// enforcing role:assign.
function allowedRoleIdsFor(workspace: MonospaceWorkspace): AllowedResources {
	return isCurrentWorkspaceRow(workspace) ? allowedRoleIds.value : allResourcesAllowed;
}

const {
	apiKeys,
	currentItemsCount,
	pageNumber,
	pageSize,
	totalItemsCount,
	isLoading: apiKeysLoading,
} = useApiKeys(() => ({
	filter: serviceAccount ? { userId: serviceAccount.id } : undefined,
}));

// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- decided once per dialog: create vs. edit, and the form object stays the same reactive instance
const formState = form;

const title = computed(() =>
	isEdit ? t('service_accounts.edit_service_account') : t('service_accounts.create_service_account'),
);

const confirmLabel = computed(() =>
	isEdit ? t('common.save') : t('common.create'),
);

const hasChanges = computed(() => form.$hasChanges() || hasWorkspaceChanges.value || hasRoleChanges.value);

const workspaceDropdownItems = computed<DropdownMenuItem[]>(() => {
	const items: DropdownMenuItem[] = availableWorkspaces.value.map((workspace) => ({
		label: getWorkspaceDisplayName(workspace),
		onSelect: () => handleAddWorkspace(workspace.id),
	}));

	return items;
});

function handleAddWorkspace(workspaceId: Uuid) {
	const workspace = availableWorkspaces.value.find((p) => p.id === workspaceId);

	if (workspace) {
		addWorkspace(workspace);
	}
}

async function handleSubmit() {
	const savedWorkspaces = [...assignedWorkspaces.value];
	const rolesByWorkspaceId: Record<string, string[]> = {};

	for (const workspace of savedWorkspaces) {
		if (isAddedWorkspace(workspace)) {
			rolesByWorkspaceId[workspace.id] = [...newWorkspaceRoles[workspace.id] ?? []];
		}
	}

	const saveAmbientRoles = isCurrentWorkspaceExistingMember.value && isCurrentWorkspaceAssigned.value;
	const roleChanges = captureRoleChanges({
		assignableRoleIds: allowedRoleIds.value,
	});

	const submit = async () => {
		const serviceAccount = await form.$submit();

		await saveChanges({
			assignedWorkspaces: savedWorkspaces,
			rolesByWorkspaceId,
			serviceAccountId: serviceAccount.id,
		});

		if (saveAmbientRoles) {
			await saveRoleChanges({ changes: roleChanges, serviceAccountId: serviceAccount.id });
		}

		if (!workspaceHandle || workspaceHandle.isActive()) {
			emit('close', isEdit ? true : serviceAccount);
		}
	};

	return workspaceHandle ? workspaceHandle.run(submit) : submit();
}
</script>

<template>
	<DDialog
		v-bind="forwarded"
		:title
		:confirm="{
			label: confirmLabel,
			disabled: !hasChanges,
			loading: form.$loading,
			onClick: handleSubmit,
		}"
		:ui="{ content: 'sm:max-w-2xl' }"
	>
		<template #body>
			<DForm
				:state="form"
				:schema="form.$schema"
				layout="stacked"
				:validate-on="[]"
				auto-focus-first-input
				@submit="handleSubmit"
			>
				<DFormField
					name="fullName"
					:label="$t('common.name')"
					required
				>
					<DInput
						v-model="formState.fullName"
						:placeholder="$t('service_accounts.name_placeholder')"
					/>
				</DFormField>

				<DFormField :label="$t('service_accounts.workspaces')">
					<ListCard
						:items="assignedWorkspaces"
						size="xs"
						:ui="{
							item: {
								base: 'cursor-auto pe-2',
								label: 'font-medium',
							},
						}"
					>
						<template #header>
							<span v-if="assignedWorkspaces.length > 0" class="text-xs font-medium text-toned">{{ $t('common.n_items', assignedWorkspaces.length) }}</span>
							<span v-else class="text-xs font-medium text-toned">{{ $t('service_accounts.no_workspaces') }}</span>
						</template>
						<template #header-actions>
							<DTooltip :text="$t('service_accounts.no_workspaces_available')" :disabled="workspaceDropdownItems.length > 0">
								<!-- TODO: div necessary because Nuxt UI tooltip doesnt't play nice with dropdown menu -->
								<div>
									<DDropdownMenu
										:items="workspaceDropdownItems"
										:ui="{ item: 'min-w-32' }"
									>
										<DButton
											color="neutral"
											variant="ghost"
											size="sm"
											icon="lucide:plus"
											:disabled="workspaceDropdownItems.length === 0"
										>
											{{ $t('service_accounts.add_workspace') }}
										</DButton>
									</DDropdownMenu>
								</div>
							</DTooltip>
						</template>
						<template #item-label="{ item }">
							{{ getWorkspaceDisplayName(item) }}
						</template>

						<template #item-trailing="{ item }">
							<div class="flex items-center gap-2">
								<WorkspaceRoleSelect
									v-if="showRolePicker(item)"
									:model-value="rolesFor(item)"
									:workspace-slug="item.apiName"
									:allowed-role-ids="allowedRoleIdsFor(item)"
									:placeholder="$t('service_accounts.roles_placeholder')"
									size="sm"
									class="w-48"
									@update:model-value="(roleIds) => setRolesFor(item, roleIds)"
								/>
								<ButtonRemove
									class="opacity-0 group-hover/list-item:opacity-100 group-focus-visible/list-item:opacity-100"
									with-leading-icon
									:trash-bin="false"
									square
									@click="removeWorkspace(item)"
								/>
							</div>
						</template>
					</ListCard>
				</DFormField>

				<DFormField v-if="serviceAccount" :label="$t('organization.dashboard.settings.access.api_keys.title')">
					<LazyApiKeyList
						v-model:page="pageNumber"
						v-model:page-size="pageSize"
						:api-keys
						:current-items-count
						:total-items-count
						:is-loading="apiKeysLoading"
						:service-account
					/>
				</DFormField>
			</DForm>
		</template>
	</DDialog>
</template>
